import React, { useState, useRef, useEffect, useCallback } from 'react'
import '../../App.css'
import { useDispatch, useSelector } from 'react-redux'
import switchIcon from '../../assets/icons/switch_icon.png'
import captureIcon from '../../assets/icons/camera_icon.png'
import streamIcon from '../../assets/icons/video_icon.png'
import Webcam from 'react-webcam'
import { debounce } from 'lodash'
import { setSegmentationResponseImgSrc, setAnalyseButtonStatus } from '../../store'
import AlertMessage from '../layout/AlertMessage'
import { toUnicodeVariant } from '../../utils'

const Camera = ({ mode, onCapture }) => {
  const webcamRef = useRef(null)
  const canvasRef = useRef(null)
  const isMountedRef = useRef(false)
  const animationFrameRef = useRef(null)
  const webcamLoadedTimeoutRef = useRef(null)

  const [isStreaming, setIsStreaming] = useState(false)
  const [capturedImage, setCapturedImage] = useState(null)
  const [isCaptured, setIsCaptured] = useState(false)
  const [showVideo, setShowVideo] = useState(true)
  const [showAlert, setShowAlert] = useState(false)
  const [imgSrc, setImgSrc] = useState(null)

  const segmentedImage = useSelector((state) => state.segmentedImage)
  const isSubmit = useSelector((state) => state.isSubmit)
  const isAnalyzeDisabled = useSelector((state) => state.isAnalyzeDisabled)
  const language = useSelector((state) => state.language)
  const translations = useSelector((state) => state.translationsData) || {}

  const dispatch = useDispatch()
  const Konva = window.Konva

  const FACING_MODE_USER = 'user'
  const FACING_MODE_ENVIRONMENT = 'environment'

  const moveableRectX = useRef('100')
  const moveableRectY = useRef('50')
  const moveableWidth = useRef('200')
  const moveableHeight = useRef('200')

  const [showCameraPermissionAlert, setShowCameraPermissionAlert] = useState(false)
  const [isWebcamLoading, setIsWebcamLoading] = useState(false)
  const [showWebcamLoadedAlert, setShowWebcamLoadedAlert] = useState(false)
  const [screenWidth, setScreenWidth] = useState(window.screen.width)

  const initialContainerWidth = Math.min(400, Math.max(screenWidth * 0.98, 1))
  const aspectRatio = 16 / 9
  const initialCalculatedHeight = initialContainerWidth / aspectRatio
  const [containerWidth] = useState(initialContainerWidth)
  const [calculatedHeight] = useState(initialCalculatedHeight)

  const [facingMode, setFacingMode] = useState(FACING_MODE_USER)

  const videoConstraints = {
    width: 680,
    height: 1000,
    facingMode,
  }

  const getTranslationText = (
    keyPath,
    data = translations,
    lang = language.toUpperCase(),
  ) => {
    const keys = keyPath.split('.')
    let result = data

    for (let i = 0; i < keys.length; i++) {
      const key = keys[i]
      if (Array.isArray(result)) {
        const found = result.find((item) => item.label === key)
        if (!found) return undefined
        result = found
      } else if (result[key] === undefined) {
        return undefined
      } else {
        result = result[key]
      }
    }

    if (
      typeof result === 'object' &&
      !Array.isArray(result) &&
      result[lang] !== undefined
    ) {
      return result[lang]
    }

    return result
  }

  const captureImageAlert = getTranslationText(
    'hairSegmentation.instructions.capture_image_check',
  )
  const captureButton = getTranslationText('hairSegmentation.actions.capture')
  const analyzeButton = getTranslationText('hairSegmentation.actions.analyze')
  const streamButton = getTranslationText('hairSegmentation.actions.stream')
  const switchButton = getTranslationText('hairSegmentation.actions.switch')
  const cameraPermissionMessage = getTranslationText(
    'hairSegmentation.instructions.cameraPermissionMessage',
  )
  const webcamLoadingMessage =
    getTranslationText('hairSegmentation.instructions.webcamLoadingMessage') ||
    'Loading camera...'
  const webcamLoadedMessage =
    getTranslationText('hairSegmentation.instructions.webcamLoadedMessage') ||
    'Camera loaded and streaming.'

  const stopCurrentStream = useCallback(() => {
    const stream = webcamRef.current?.video?.srcObject
    if (stream && typeof stream.getTracks === 'function') {
      stream.getTracks().forEach((track) => track.stop())
    }
  }, [])

  const debouncedUpdateOuterRect = debounce((moveableRectCoords) => {
    dispatch({ type: 'SET_MOVEABLE_RECT_COORDS', payload: moveableRectCoords })
  }, 1000)

  const startStream = useCallback(() => {
    if (!isMountedRef.current) return

    dispatch(setAnalyseButtonStatus(true))
    setIsCaptured(false)
    setIsStreaming(true)
    setShowVideo(true)
    setCapturedImage(null)
    dispatch(setAnalyseButtonStatus(false))
    dispatch(setSegmentationResponseImgSrc(null))
    dispatch({ type: 'SET_API_RESPONSE_IMG_SRC', payload: null })
    dispatch({ type: 'SET_CAPTURED_IMAGE', payload: null })

    moveableRectX.current = '100'
    moveableRectY.current = '50'
    moveableWidth.current = '200'
    moveableHeight.current = '200'
  }, [dispatch])

  useEffect(() => {
    isMountedRef.current = true

    dispatch(setSegmentationResponseImgSrc(null))
    startStream()
    setCapturedImage(null)
    setShowVideo(true)
    setIsCaptured(false)

    return () => {
      isMountedRef.current = false

      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
      }

      if (webcamLoadedTimeoutRef.current) {
        clearTimeout(webcamLoadedTimeoutRef.current)
      }

      stopCurrentStream()
      debouncedUpdateOuterRect.cancel()
    }
  }, [dispatch, startStream, stopCurrentStream])

  const switchCamera = useCallback(() => {
    if (!isMountedRef.current) return

    dispatch(setSegmentationResponseImgSrc(null))
    setIsStreaming(true)
    setIsCaptured(false)
    setShowVideo(true)
    setCapturedImage(null)

    setFacingMode((prevState) =>
      prevState === FACING_MODE_USER
        ? FACING_MODE_ENVIRONMENT
        : FACING_MODE_USER,
    )
  }, [dispatch])

  const captureImage = () => {
    if (!isMountedRef.current) return

    if (isCaptured) {
      setShowAlert(true)
      return
    }

    dispatch(setSegmentationResponseImgSrc(null))
    setIsCaptured(true)
    setIsStreaming(false)
    setShowVideo(false)
    setCapturedImage(null)

    const video = webcamRef.current
    const canvas = canvasRef.current

    if (!video || !canvas || !video.video) return

    const ctx = canvas.getContext('2d')
    ctx.clearRect(0, 0, containerWidth, calculatedHeight)
    ctx.drawImage(video.video, 0, 0, containerWidth, calculatedHeight)

    const capturedImageDataUrl = canvas.toDataURL('image/png', 1.0)

    if (!isMountedRef.current) return

    setCapturedImage(capturedImageDataUrl)
    if (onCapture) onCapture(capturedImageDataUrl)

    dispatch({ type: 'SET_CAPTURED_IMAGE', payload: capturedImageDataUrl })
    dispatch({
      type: 'SET_OVERLAY_COORDINATES',
      payload: [100, 50, 200, 200],
    })

    if (mode === 'manual') {
      ctx.strokeRect(
        parseInt(moveableRectX.current),
        parseInt(moveableRectY.current),
        parseInt(moveableWidth.current),
        parseInt(moveableHeight.current),
      )
      setImgSrc(canvas.toDataURL('image/jpeg', 1.0))
    }
  }

  const handleUserMedia = useCallback(() => {
    if (!isMountedRef.current) return

    setIsWebcamLoading(false)

    if (webcamLoadedTimeoutRef.current) {
      clearTimeout(webcamLoadedTimeoutRef.current)
    }

    webcamLoadedTimeoutRef.current = setTimeout(() => {
      if (isMountedRef.current) {
      }
    }, 3000)
  }, [])

  const handleUserMediaError = useCallback(() => {
    if (!isMountedRef.current) return

    setIsStreaming(false)
    setShowVideo(false)
    setIsWebcamLoading(false)
    setShowCameraPermissionAlert(true)
  }, [])

  const drawFixedRect = useCallback(() => {
    if (!isMountedRef.current) return

    const video = webcamRef.current
    const canvas = canvasRef.current

    if (video && canvas && isStreaming && video.video) {
      const ctx = canvas.getContext('2d')
      ctx.drawImage(video.video, 0, 0, containerWidth, calculatedHeight)
      ctx.lineWidth = 3
      ctx.strokeStyle = 'green'
    }

    if (isStreaming) {
      animationFrameRef.current = requestAnimationFrame(drawFixedRect)
    }
  }, [isStreaming, containerWidth, calculatedHeight])

  useEffect(() => {
    if (isStreaming) {
      animationFrameRef.current = requestAnimationFrame(drawFixedRect)
    }

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
      }
    }
  }, [isStreaming, containerWidth, calculatedHeight, drawFixedRect])

  function addAnchor(group, x, y, name, update, restrict, drag, ownShape = '') {
    var stage = group.getStage()
    var layer = group.getLayer()
    var anchor = new Konva.Circle({
      x: x,
      y: y,
      stroke: 'red',
      strokeWidth: 2,
      radius: 8,
      name: name,
      id: name,
      draggable: !drag,
      dragOnTop: true,
      dragBoundFunc: (pos) => {
        return restrict(pos, group, ownShape)
      },
    })

    anchor.on('mouseenter', function () {
      stage.container().style.cursor = 'pointer'
    })
    anchor.on('mouseleave', function () {
      stage.container().style.cursor = 'default'
    })
    anchor.on('dragmove', function () {
      update(this, ownShape)
      layer.draw()
    })
    anchor.on('mousedown touchstart', function () {
      group.setDraggable(drag)
    })
    anchor.on('dragend', function () {
      group.setDraggable(drag)
      layer.draw()
    })
    anchor.on('touchstart tap', function () {
      var layer = this.getLayer()
      document.body.style.cursor = 'pointer'
      this.radius(40)
      layer.draw()
    })
    anchor.on('touchend', function () {
      var layer = this.getLayer()
      document.body.style.cursor = 'default'
      this.radius(8)
      layer.draw()
    })

    group.add(anchor)

    stage.on('touchend', () => {
      var layer = anchor.getLayer()
      document.body.style.cursor = 'default'
      anchor.radius(8)
      layer.draw()
    })
  }

  function updateOuterRect(activeAnchor) {
    var group = activeAnchor.getParent()
    var topLeft = group.find('.topLeft')[0]
    var bottomRight = group.find('.bottomRight')[0]
    var rect = group.findOne('.outerRect')

    rect.position(topLeft.position())

    var width = bottomRight.getX() - topLeft.getX()
    var height = bottomRight.getY() - topLeft.getY()

    rect.width(width)
    rect.height(height)

    moveableWidth.current = width
    moveableHeight.current = height

    const moveableRectCoords = [
      parseInt(group.absolutePosition().x + topLeft.getX()),
      parseInt(group.absolutePosition().y + topLeft.getY()),
      parseInt(width),
      parseInt(height),
    ]

    debouncedUpdateOuterRect(moveableRectCoords)
  }

  function moveTopLeft(pos, group) {
    var bottomRight = group.findOne('.bottomRight')
    var absPos = bottomRight.absolutePosition()

    pos.x =
      pos.x > absPos.x - group.absolutePosition().x
        ? absPos.x - group.absolutePosition().x
        : pos.x
    pos.y =
      pos.y > absPos.y - group.absolutePosition().y
        ? absPos.y - group.absolutePosition().y
        : pos.y

    return pos
  }

  function moveBottomRight(pos, group) {
    var topLeft = group.findOne('.topLeft')
    var absPos = topLeft.absolutePosition()

    pos.x =
      pos.x < absPos.x - group.absolutePosition().x
        ? absPos.x - group.absolutePosition().x
        : pos.x
    pos.y =
      pos.y < absPos.y - group.absolutePosition().y
        ? absPos.y - group.absolutePosition().y
        : pos.y

    return pos
  }

  function resizeRect() {
    if (!capturedImage || !Konva) return

    const tempContainer = document.getElementById('temp-container')
    if (!tempContainer) return

    tempContainer.innerHTML = ''

    var stage = new Konva.Stage({
      container: 'temp-container',
      width: containerWidth,
      height: calculatedHeight,
      x: 0,
      y: 0,
      visible: true,
    })

    var imageObj = new Image()
    var bgimage = new Konva.Image({
      x: 0,
      y: 0,
      image: imageObj,
      width: containerWidth,
      height: calculatedHeight,
    })

    imageObj.onload = function () {
      if (!isMountedRef.current) return
      bgimage.cache()
      bgimage.draw()
    }

    imageObj.src = capturedImage

    var layer = new Konva.Layer()
    stage.add(layer)
    layer.add(bgimage)

    if (!segmentedImage) {
      var initialRectWidth = Math.min(200, containerWidth * 0.5)
      var initialRectHeight = Math.min(200, calculatedHeight * 0.5)
      var initialRectX = (containerWidth - initialRectWidth) / 2
      var initialRectY = (calculatedHeight - initialRectHeight) / 2

      var doubleRectGroup = new Konva.Group({
        x: initialRectX,
        y: initialRectY,
        draggable: true,
        width: containerWidth,
        height: calculatedHeight,
      })

      doubleRectGroup.on('dragend', function () {
        moveableRectX.current = this.absolutePosition().x
        moveableRectY.current = this.absolutePosition().y
      })

      layer.add(doubleRectGroup)

      var outerRect = new Konva.Rect({
        x: 0,
        y: 0,
        width: initialRectWidth,
        height: initialRectHeight,
        stroke: 'green',
        strokeWidth: 3,
        name: 'outerRect',
      })

      doubleRectGroup.add(outerRect)

      addAnchor(
        doubleRectGroup,
        0,
        0,
        'topLeft',
        updateOuterRect,
        moveTopLeft,
        false,
      )
      addAnchor(
        doubleRectGroup,
        initialRectWidth,
        initialRectHeight,
        'bottomRight',
        updateOuterRect,
        moveBottomRight,
        false,
      )
      addAnchor(
        doubleRectGroup,
        0,
        0,
        'drag',
        updateOuterRect,
        (pos) => pos,
        true,
      )

      layer.draw()
    }
  }

  useEffect(() => {
    const webcamContainer = document.getElementById('webcam-container')
    const tempContainer = document.getElementById('temp-container')

    if (!webcamContainer || !tempContainer) return

    if (mode === 'manual' && isCaptured === true && isStreaming === false) {
      webcamContainer.style.display = 'none'
      tempContainer.style.display = 'inline-block'
      resizeRect()
    } else if (!isCaptured && isStreaming) {
      webcamContainer.style.display = 'inline-block'
      tempContainer.style.display = 'none'
    } else if (isCaptured && !isStreaming) {
      webcamContainer.style.display = 'block'
      tempContainer.style.display = 'none'
    }
  }, [
    mode,
    isCaptured,
    isStreaming,
    containerWidth,
    calculatedHeight,
    capturedImage,
  ])

  useEffect(() => {
    if (segmentedImage && !isStreaming && isMountedRef.current) {
      setCapturedImage(segmentedImage)
    }
  }, [segmentedImage, isStreaming])

  useEffect(() => {
    if (!segmentedImage || isStreaming || !isMountedRef.current) return

    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    const img = new Image()

    img.onload = () => {
      if (!isMountedRef.current) return
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
    }

    img.src = segmentedImage
  }, [segmentedImage, isStreaming, containerWidth, calculatedHeight])

  return (
    <div className='camera'>
      <div
        id='temp-container'
        className='temp-container'
        style={{
          display: mode === 'manual' && isCaptured ? 'inline-block' : 'none',
          width: containerWidth,
          height: calculatedHeight,
        }}
      />

      <div
        id='webcam-container'
        className='webcam-container'
        style={{
          display: !isCaptured ? 'inline-block' : 'block',
          width: containerWidth,
          height: calculatedHeight,
          backgroundColor: 'white',
        }}
      >
        {!segmentedImage && isCaptured && (
          <img
            className='mainImg'
            src={capturedImage}
            alt='Captured'
            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'cover' }}
          />
        )}

        {segmentedImage && !isStreaming && (
          <img
            className='mainImg'
            src={segmentedImage}
            alt='Segmented'
            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'cover' }}
          />
        )}

        {!isCaptured && (
          <Webcam
            ref={webcamRef}
            screenshotFormat='image/jpeg'
            videoConstraints={videoConstraints}
            width={containerWidth}
            height={calculatedHeight}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              maxWidth: '400px',
              height: '100%',
              objectFit: 'cover',
            }}
            onUserMedia={handleUserMedia}
            onUserMediaError={handleUserMediaError}
          />
        )}

        <canvas
          ref={canvasRef}
          id='canvas'
          width={containerWidth}
          height={calculatedHeight}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            maxWidth: '400px',
            height: '100%',
          }}
        />
      </div>

      <div
        style={{
          position: 'relative',
          top: '20px',
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          width: containerWidth,
        }}
      >
        <button
          onClick={switchCamera}
          style={{
            margin: '0 10px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            backgroundColor: '#D3D3D3',
            width: '70px',
            height: '70px',
          }}
        >
          <span style={{ color: 'black' }}>{switchButton}</span>
          <img
            src={switchIcon}
            alt='Switch'
            style={{
              width: '32px',
              height: '32px',
              verticalAlign: 'middle',
              marginTop: '5px',
              backgroundColor: '#D3D3D3',
              borderColor: '#D3D3D3',
            }}
          />
        </button>

        <button
          onClick={captureImage}
          style={{
            margin: '0 10px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            backgroundColor: '#D3D3D3',
            width: '70px',
            height: '70px',
          }}
        >
          <span style={{ color: 'black' }}>{captureButton}</span>
          <img
            src={captureIcon}
            alt='Capture'
            style={{
              width: '32px',
              height: '32px',
              verticalAlign: 'middle',
              marginTop: '5px',
              backgroundColor: '#D3D3D3',
              borderColor: '#D3D3D3',
            }}
          />
        </button>

        <button
          onClick={startStream}
          style={{
            margin: '0 10px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            backgroundColor: '#D3D3D3',
            width: '70px',
            height: '70px',
          }}
        >
          <span style={{ color: 'black' }}>{streamButton}</span>
          <img
            src={streamIcon}
            alt='Stream'
            style={{
              width: '32px',
              height: '32px',
              verticalAlign: 'middle',
              marginTop: '5px',
              backgroundColor: '#D3D3D3',
              borderColor: '#D3D3D3',
            }}
          />
        </button>
      </div>

      {showAlert && (
        <AlertMessage
          message={
            captureImageAlert[0] +
            toUnicodeVariant(captureButton, 'bold sans', 'bold') +
            captureImageAlert[1]
          }
          onConfirm={() => {
            setShowAlert(false)
          }}
        />
      )}

      {showCameraPermissionAlert && (
        <AlertMessage
          message={cameraPermissionMessage}
          onConfirm={() => {
            setShowCameraPermissionAlert(false)
          }}
        />
      )}

      {isWebcamLoading && <AlertMessage message={webcamLoadingMessage} />}

      {showWebcamLoadedAlert && (
        <AlertMessage
          message={webcamLoadedMessage}
          onConfirm={() => {
            setShowWebcamLoadedAlert(false)
          }}
        />
      )}
    </div>
  )
}

export default Camera